import { Portal } from '@/components/ui/portal';
import { MENU_ITEMS } from '@/constants/navigation';
import { useAuthStore } from '@/stores/auth-store';
import { openEmailContact } from '@/utils/mail';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Linking, Pressable, StyleSheet, View, useWindowDimensions } from 'react-native';
import Animated, { FadeIn, FadeOut, SlideInRight, SlideOutRight } from 'react-native-reanimated';
import { LanguageSelector } from './language-selector';
import { Text } from './text';
import { UserMenuContent } from './user-menu-content';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  pathname: string;
}

export function MobileMenu({ isOpen, onClose, pathname }: MobileMenuProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const { height } = useWindowDimensions();
  const { isLoggedIn, logout } = useAuthStore();

  if (!isOpen) return null;

  const handleNavigate = (path: string) => {
    onClose();
    if (path.startsWith('http')) {
      Linking.openURL(path);
    } else if (path.startsWith('mailto:')) {
      openEmailContact();
    } else {
      router.push(path as any);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      onClose();
      router.replace('/');
    } catch (error) {
      console.error('Logout failed', error);
    }
  };

  return (
    <Portal>
      <View style={StyleSheet.absoluteFill}>
        <Animated.View entering={FadeIn} exiting={FadeOut} style={styles.overlay}>
          <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
        </Animated.View>

        <Animated.View
          entering={SlideInRight.springify().damping(20)}
          exiting={SlideOutRight.springify().damping(20)}
          style={[styles.drawer, { height }]}>
          <View style={styles.header}>
            <Text type='defaultSemiBold' style={{ fontSize: 18 }}>
              Menu
            </Text>
            <Pressable onPress={onClose} style={styles.closeButton}>
              <Ionicons name='close' size={28} color='#000' />
            </Pressable>
          </View>

          <View style={styles.content}>
            {MENU_ITEMS.map(item => {
              const isActive = pathname === item.path;
              return (
                <Pressable
                  key={item.key}
                  onPress={() => handleNavigate(item.path)}
                  style={[styles.menuItem, isActive && styles.activeMenuItem]}>
                  <Text
                    type={isActive ? 'defaultSemiBold' : 'default'}
                    style={[styles.menuText, isActive && styles.activeMenuText]}>
                    {t(item.key)}
                  </Text>
                  {isActive && <View style={styles.activeIndicator} />}
                </Pressable>
              );
            })}
          </View>

          <View style={styles.footer}>
            <View style={styles.divider} />
            <View style={styles.languageSection}>
              <Text type='defaultSemiBold' style={{ marginBottom: 12 }}>
                {t('language.title', 'Idioma')}
              </Text>
              <LanguageSelector />
            </View>
          </View>
          {isLoggedIn && (
            <View style={styles.footer}>
              <View style={styles.divider} />
              <View style={styles.languageSection}>
                <Text type='defaultSemiBold' style={{ marginBottom: 12 }}>
                  {t('common.user')}
                </Text>
                <UserMenuContent onLogout={handleLogout} />
              </View>
            </View>
          )}
        </Animated.View>
      </View>
    </Portal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  drawer: {
    position: 'absolute',
    right: 0,
    top: 0,
    width: 280,
    backgroundColor: '#fff',
    shadowColor: '#000',
    shadowOffset: { width: -2, height: 0 },
    shadowOpacity: 0.1,
    shadowRadius: 10,
    elevation: 5,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    paddingTop: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  closeButton: {
    padding: 4,
  },
  content: {
    padding: 10,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    paddingHorizontal: 15,
    borderRadius: 12,
    marginVertical: 4,
  },
  activeMenuItem: {
    backgroundColor: '#f8f9fa',
  },
  menuText: {
    fontSize: 16,
    color: '#333',
  },
  activeMenuText: {
    color: '#000',
  },
  activeIndicator: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#000',
  },
  footer: {
    paddingBottom: 40,
  },
  divider: {
    height: 1,
    backgroundColor: '#f0f0f0',
    marginHorizontal: 20,
    marginBottom: 20,
  },
  languageSection: {
    paddingHorizontal: 20,
  },
});
